import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import { Icon, IconTypes } from '../Icon';
import { IButtonProps } from './ButtonProps';

interface ButtonIconProps extends IButtonProps {
  icon: IconTypes;
  headerButton?: boolean;
  dataCy?: string;
  fill?: any;
}

const ButtonIconStyles = css<Partial<ButtonIconProps>>`
  position: relative;
  display: flex;
  flex-shrink: 0;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: ${toRem(theme.button.height.large)};
  height: ${toRem(theme.button.height.large)};
  border-radius: 50%;
  border: none;
  background-color: ${theme.color.primary[500]};
  transition: background-color ${theme.transition.button.fast};

  svg {
    width: ${toRem(16.5)};
    fill: ${theme.color.white};
  }

  &:hover {
    background-color: ${theme.color.primary[600]};
  }

  ${({ appearance }) =>
    appearance === 'destructive' &&
    css`
      background-color: ${theme.color.danger[50]};
      color: ${theme.color.white};

      svg {
        fill: ${theme.color.white} !important;
      }

      &:hover {
        background-color: ${theme.color.danger[50]};
      }
    `}

  ${({ appearance }) =>
    appearance === 'secondary' &&
    css`
      border: ${toRem(1)} solid ${theme.color.neutral[200]};
      background-color: ${theme.color.neutral[100]};

      svg {
        fill: ${theme.color.neutral[800]};
      }

      &:hover {
        background-color: ${theme.color.neutral[100]};

        svg {
          fill: ${theme.color.neutral[900]};
        }
      }
    `}

    ${({ appearance }) =>
    appearance === 'white' &&
    css`
      border: ${toRem(1)} solid ${theme.color.neutral[200]};
      background-color: ${theme.color.white};
      color: ${theme.color.neutral[900]};

      svg {
        fill: ${theme.color.neutral[800]};
      }

      &:hover {
        background-color: ${theme.color.white};

        svg {
          fill: ${theme.color.neutral[800]};
        }
      }
    `}

  ${({ size }) =>
    size === 'small' &&
    css`
      width: ${toRem(theme.button.height.small)};
      height: ${toRem(theme.button.height.small)};

      svg {
        width: ${toRem(15.12)};
      }
    `}

  ${({ headerButton }) =>
    headerButton &&
    css`
      @media (max-width: ${theme.breakpoint.small - 1}px) {
        width: ${toRem(theme.button.height.small)};
        height: ${toRem(theme.button.height.small)};

        svg {
          width: ${toRem(15.12)};
        }
      }
    `}

    &:disabled {
    background-color: ${theme.color.neutral[200]};
    color: ${theme.color.neutral[700]};
    cursor: auto;

    svg {
      fill: ${theme.color.neutral[400]};
    }

    &:hover {
      background-color: ${theme.color.neutral[200]};
      color: ${theme.color.neutral[700]};

      svg {
        fill: ${theme.color.neutral[400]};
      }
    }
  }
`;

const Layout = styled.button`
  ${ButtonIconStyles}
`;

const StyledLink = styled(({ headerButton, ...rest }) => <Link {...rest} />)<Partial<ButtonIconProps>>`
  text-decoration: none;
  ${ButtonIconStyles}
`;

const ButtonIcon: React.FC<ButtonIconProps> = ({ label, icon, isLoading, headerButton = false, type = 'button', appearance = 'secondary', size = 'small', to, onClick, className, disabled, tooltipId, dataCy, ...rest }) => {
  const loaderSize = size === 'small' ? 33 : 44;
  if (isLoading) return <Skeleton circle width={loaderSize} height={loaderSize} />;

  const iconComponent = <Icon icon={icon} tooltipId={tooltipId} />;

  if (to && !disabled) {
    return (
      <StyledLink aria-label={label} appearance={appearance} headerButton={headerButton} size={size} to={to} onClick={onClick} className={className}>
        {iconComponent}
      </StyledLink>
    );
  }
  return (
    <Layout aria-label={label} type={type} appearance={appearance} headerButton={headerButton} size={size} onClick={onClick} disabled={disabled} className={className} data-cy={dataCy} {...rest}>
      {iconComponent}
    </Layout>
  );
};

export default ButtonIcon;
